<template>
  <div class="card">
    <div class="card__header flex justify-between items-center space-x-4">
      <p>{{ $t('ProjectSettings.ProjectTab.WbsCodeList.title') }}</p>

      <button
        v-if="projectsStore.project?.unarchived"
        type="submit"
        class="btn btn--secondary-blue"
        data-test="add-wbs-code"
        @click.prevent="addWbsCode"
      >
        {{ $t('ProjectSettings.ProjectTab.WbsCodeList.addButton') }}
      </button>
    </div>

    <div class="flex w-full p-4 space-x-4">
      <ul
        v-if="wbsCodes.length"
        class="overflow-auto space-y-4 h-64 flex-1"
      >
        <li
          v-for="(wbsCode, i) in wbsCodes"
          :key="wbsCode.id"
          class="card__list-item"
          :data-test="`wbscode-item-${i}`"
        >
          <div class="grid grid-cols-2">
            <p :title="wbsCode.code">
              {{ wbsCode.code }}
            </p>

            <div class="flex justify-end items-center row-span-2">
              <button
                v-if="projectsStore.project?.unarchived"
                type="button"
                data-test="edit"
                class="btn-icon btn-icon-blue"
                @click="editCode(wbsCode)"
              >
                <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="h-6 w-6">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
</svg>
              </button>

              <button
                v-if="projectsStore.project?.unarchived"
                type="button"
                class="btn btn-icon btn-icon-red"
                data-test="trash"
                @click="removeWbsCode(wbsCode.id)"
              >
                <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-6 h-6">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
    />
</svg>
              </button>
            </div>

            <p
              class="truncate w-20 text-sm"
              :title="wbsCode.description"
            >
              {{ wbsCode.description }}
            </p>

            <p class="text-sm">
              {{ $t('ProjectSettings.ProjectTab.WbsCodeList.unit') }} {{ wbsCode.unit }}
            </p>

            <template v-if="!project?.isClient">
              <p class="text-sm text-right">
                {{ $t('ProjectSettings.ProjectTab.WbsCodeList.quantity') }} {{ $tn(wbsCode.tender_quantity) }}
              </p>

              <p class="text-sm">
                {{ $t('ProjectSettings.ProjectTab.WbsCodeList.cost') }} {{ $tcc(wbsCode.tender_cost) }}
              </p>

              <p class="text-sm text-right">
                {{ $t('ProjectSettings.ProjectTab.WbsCodeList.days') }} {{ $tn(wbsCode.tender_production_rate) }}
              </p>
            </template>
          </div>
        </li>
      </ul>

      <div
        v-else
        class="text-center font-semibold w-full p-2"
      >
        {{ $t('ProjectSettings.ProjectTab.WbsCodeList.emptyState') }}
      </div>
    </div>

    <WbsCodeModal
      v-if="showEditWbsCode"
      :wbs-code="selectedWbsCode"
      :project-id="$props.projectId"
      @close="showEditWbsCode = false"
    />
  </div>
</template>

<script setup lang="ts">
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { t } from '@/i18n';
import WbsCode from '@/models/WbsCode';
import { useProjectsStore } from '@/store/projects';
import { useWbsCodesStore } from '@/store/wbsCodes';
import Swal from 'sweetalert2';
import { computed, ref } from 'vue';
import WbsCodeModal from './WbsCodeModal.vue';

const props = defineProps<{
  projectId: string;
}>();

const wbsCodeStore = useWbsCodesStore();

const wbsCodes = computed(() => {
  return wbsCodeStore.models.where('project_id', parseInt(props.projectId)).get();
});

const projectsStore = useProjectsStore();

const project = computed(() => {
  return projectsStore.project;
});

const destroyWbsCodeAction = useStoreApiAction(wbsCodeStore.deleteCode);

const removeWbsCode = (id: Id) => {
  Swal.fire({
    icon: 'warning',
    title: t('ProjectSettings.ProjectTab.WbsCodeList.deleteTitle'),
    text: t('ProjectSettings.ProjectTab.WbsCodeList.deleteText'),
    showConfirmButton: true,
    showCancelButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      destroyWbsCodeAction.request(id).catch(() => {
        Swal.fire({
          icon: 'error',
          title: t('ProjectSettings.ProjectTab.WbsCodeList.deleteFailed'),
        });
      });
    }
  });
};

const showEditWbsCode = ref(false);
const selectedWbsCode = ref();

const editCode = (wbsCode: WbsCode) => {
  selectedWbsCode.value = wbsCode;
  showEditWbsCode.value = true;
};

const addWbsCode = () => {
  selectedWbsCode.value = undefined;
  showEditWbsCode.value = true;
};
</script>

<style scoped></style>
